import * as Cesium from 'cesium';

// --- PROPS HELPERS ---
export const getPropsObject = (propAny: any) => {
  if (!propAny) return {};
  try {
    if (typeof propAny.getValue === 'function') {
      return propAny.getValue(Cesium.JulianDate.now()) || {};
    }
    return propAny;
  } catch {
    return propAny;
  }
};

export const readColor = (v: any, fallback = '#ffffff') => {
  try {
    if (!v) return Cesium.Color.fromCssColorString(fallback);
    return Cesium.Color.fromCssColorString(String(v));
  } catch {
    return Cesium.Color.fromCssColorString(fallback);
  }
};

export const toAlpha = (v: any, defaultAlpha = 1) => {
  if (v === undefined || v === null || v === '') return defaultAlpha;
  const n = Number(v);
  if (isNaN(n)) return defaultAlpha;

  return n > 1 ? Math.min(Math.max(n / 100, 0), 1) : Math.min(Math.max(n, 0), 1);
};

// --- ENTITY STYLING ---
export const applyEntityStyles = (entity: Cesium.Entity, style: any, rawProps: any) => {
  // POLYLINE
  if (entity.polyline) {
    const stroke = style.lineColour || style.color || style.stroke || rawProps.color;
    const strokeOpacity = toAlpha(style.lineOpacity ?? style.opacity, 1);
    const strokeWidth = Number(style?.strokeWidth ?? style.lineWeight ?? style.width ?? 2) || 2;
    entity.polyline.width = new Cesium.ConstantProperty(strokeWidth);
    entity.polyline.material = new Cesium.ColorMaterialProperty(readColor(stroke || '#00FFFF').withAlpha(strokeOpacity));
  }

  // POLYGON
  if (entity.polygon) {
    const fill = style.fill || style.color || rawProps.fill || '#ff0000';
    const fillOpacity = toAlpha(style.fillOpacity ?? style.opacity, 1);
    entity.polygon.material = new Cesium.ColorMaterialProperty(readColor(fill).withAlpha(fillOpacity));

    const outlineWeight = Number(style.outlineWeight ?? 2);
    const outlineEnabled = style.polygonOutline ?? true;
    const outlineColor = style.outline || style.outlineColor || style.lineColour || '#000000';
    const outlineOpacity = toAlpha(style.outlineOpacity ?? 1, 1);

    entity.polygon.outline = new Cesium.ConstantProperty(false);

    if (outlineEnabled && outlineWeight > 0) {
      if (!entity.corridor) {
        entity.corridor = new Cesium.CorridorGraphics();
      }

      entity.corridor.positions = new Cesium.CallbackProperty(() => {
        const positions = extractPositionsFromEntity(entity);
        if (!positions || positions.length < 2) return undefined;

        return positions.length > 2 ? [...positions, positions[0]] : positions;
      }, false);

      entity.corridor.width = new Cesium.ConstantProperty(outlineWeight);

      entity.corridor.height = new Cesium.ConstantProperty(0);
      entity.corridor.cornerType = new Cesium.ConstantProperty(Cesium.CornerType.MITERED);
      entity.corridor.show = new Cesium.ConstantProperty(true);

      entity.corridor.material = new Cesium.ColorMaterialProperty(readColor(outlineColor).withAlpha(outlineOpacity));
    } else {
      if (entity.corridor) {
        entity.corridor.show = new Cesium.ConstantProperty(false);
      }
    }
  }

  // POINT
  if (rawProps._type === 'point') {
    if (entity.billboard) entity.billboard = undefined;
    if (!entity.point) entity.point = new Cesium.PointGraphics();

    const fill = style.fill ?? '#ffffff';
    const fillOpacity = toAlpha(style.fillOpacity ?? 100, 1);
    const outline = style.outline ?? '#000000';
    const outlineOpacity = toAlpha(style.outlineOpacity ?? 100, 1);
    const outlineWeight = Number(style.outlineWeight ?? 2) || 2;

    entity.point.pixelSize = new Cesium.ConstantProperty(15);
    entity.point.color = new Cesium.ConstantProperty(readColor(fill).withAlpha(fillOpacity));
    entity.point.outlineColor = new Cesium.ConstantProperty(readColor(outline).withAlpha(outlineOpacity));
    entity.point.outlineWidth = new Cesium.ConstantProperty(outlineWeight);
  }

  // TEXT SKETCH
  if (rawProps._type === 'text') {
    const labelText = rawProps.label ?? style.label ?? '';

    if (entity.billboard) entity.billboard = undefined;
    if (!entity.point) entity.point = new Cesium.PointGraphics();

    entity.point.pixelSize = new Cesium.ConstantProperty(10);
    entity.point.color = new Cesium.ConstantProperty(Cesium.Color.WHITE);
    entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK);
    entity.point.outlineWidth = new Cesium.ConstantProperty(2);

    if (!entity.label) entity.label = new Cesium.LabelGraphics();

    const fontSize = style.fontSize ?? 12;
    const fontStyle = style.fontStyle ?? 'monospace';
    const textColour = style.textColour ?? '#ffffff';
    const haloEnabled = style.halo ?? true;
    const haloColour = style.haloColour ?? '#000000';
    const haloStrength = style.haloStrength ?? 2;

    entity.label.text = new Cesium.ConstantProperty(String(labelText));
    entity.label.font = new Cesium.ConstantProperty(`${fontSize}px ${fontStyle}`);
    entity.label.fillColor = new Cesium.ConstantProperty(readColor(textColour));
    entity.label.style = new Cesium.ConstantProperty(haloEnabled ? Cesium.LabelStyle.FILL_AND_OUTLINE : Cesium.LabelStyle.FILL);
    entity.label.outlineColor = new Cesium.ConstantProperty(readColor(haloColour));
    entity.label.outlineWidth = new Cesium.ConstantProperty(haloEnabled ? Number(haloStrength) || 2 : 0);
    entity.label.verticalOrigin = new Cesium.ConstantProperty(Cesium.VerticalOrigin.BOTTOM);
    entity.label.horizontalOrigin = new Cesium.ConstantProperty(Cesium.HorizontalOrigin.CENTER);
    entity.label.pixelOffset = new Cesium.ConstantProperty(new Cesium.Cartesian2(0, -9));
  }
};

export const extractPositionsFromEntity = (entity: Cesium.Entity): Cesium.Cartesian3[] => {
  const now = Cesium.JulianDate.now();
  // POLYGON
  if (entity.polygon?.hierarchy) {
    const hierarchy = entity.polygon.hierarchy.getValue(now);
    if (!hierarchy?.positions) return [];
    const positions = hierarchy.positions.slice();
    if (positions.length > 2) {
      const first = positions[0];
      const last = positions[positions.length - 1];
      if (Cesium.Cartesian3.equalsEpsilon(first, last, Cesium.Math.EPSILON7)) {
        positions.pop();
      }
    }
    return positions;
  }
  // LINE
  if (entity.polyline?.positions) {
    const value = entity.polyline.positions.getValue(now);
    return value ? [...value] : [];
  }
  // POINT / TEXT
  if (entity.position) {
    const pos = entity.position.getValue(now);
    return pos ? [pos] : [];
  }
  return [];
};

// --- EDITING LOGIC ---
const getWorldPosition = (viewer: Cesium.Viewer, movement: any) => {
  const ray = viewer.camera.getPickRay(movement.endPosition || movement.position);
  if (!ray) return null;
  return viewer.scene.globe.pick(ray, viewer.scene);
};

const createVertexHandles = (viewer: Cesium.Viewer, positions: Cesium.Cartesian3[], colorString: string) => {
  const handleColor = colorString ? Cesium.Color.fromCssColorString(colorString) : Cesium.Color.YELLOW;
  return positions.map((_, index) =>
    viewer.entities.add({
      position: new Cesium.CallbackProperty(() => positions[index], false) as unknown as Cesium.PositionProperty,
      point: {
        pixelSize: 12,
        color: handleColor,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 2,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      properties: {
        vertexIndex: index,
      },
    }),
  );
};

export const makePolygonEditable = (
  viewer: Cesium.Viewer,
  polygonEntity: Cesium.Entity,
  positions: Cesium.Cartesian3[],
  color: string,
  onEditCallback: () => void,
) => {
  polygonEntity.polygon!.hierarchy = new Cesium.CallbackProperty(() => new Cesium.PolygonHierarchy(positions), false);

  const vertexEntities = createVertexHandles(viewer, positions, color);
  let draggingVertex: any = null;
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  const disableCamera = () => {
    const c = viewer.scene.screenSpaceCameraController;
    c.enableRotate = c.enableZoom = c.enableTranslate = false;
  };
  const enableCamera = () => {
    const c = viewer.scene.screenSpaceCameraController;
    c.enableRotate = c.enableZoom = c.enableTranslate = true;
  };

  handler.setInputAction((movement: any) => {
    const picked = viewer.scene.pick(movement.position);
    const entity = (picked?.id as any) || (picked?.primitive as any)?.id;
    if (!entity?.properties?.vertexIndex) return;
    draggingVertex = entity;
    disableCamera();
  }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

  handler.setInputAction((movement: any) => {
    if (!draggingVertex) return;
    const pos = getWorldPosition(viewer, movement);
    if (!pos) return;
    const index = draggingVertex.properties.vertexIndex.getValue();
    positions[index] = pos;
    onEditCallback();
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(() => {
    draggingVertex = null;
    enableCamera();
  }, Cesium.ScreenSpaceEventType.LEFT_UP);

  return {
    destroy: () => {
      handler.destroy();
      vertexEntities.forEach((e) => viewer.entities.remove(e));
    },
  };
};

export const makePolylineEditable = (
  viewer: Cesium.Viewer,
  lineEntity: Cesium.Entity,
  positions: Cesium.Cartesian3[],
  color: string,
  onEditCallback: () => void,
) => {
  lineEntity.polyline!.positions = new Cesium.CallbackProperty(() => positions, false);

  const vertexEntities = createVertexHandles(viewer, positions, color);
  let draggingVertex: any = null;
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  const disableCamera = () => {
    const c = viewer.scene.screenSpaceCameraController;
    c.enableRotate = c.enableZoom = c.enableTranslate = false;
  };
  const enableCamera = () => {
    const c = viewer.scene.screenSpaceCameraController;
    c.enableRotate = c.enableZoom = c.enableTranslate = true;
  };

  handler.setInputAction((movement: any) => {
    const picked = viewer.scene.pick(movement.position);
    const entity = picked?.id as any;
    if (!entity?.properties?.vertexIndex) return;
    draggingVertex = entity;
    disableCamera();
  }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

  handler.setInputAction((movement: any) => {
    if (!draggingVertex) return;
    const pos = getWorldPosition(viewer, movement);
    if (!pos) return;
    const index = draggingVertex.properties.vertexIndex.getValue();
    positions[index] = pos;
    onEditCallback();
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(() => {
    draggingVertex = null;
    enableCamera();
  }, Cesium.ScreenSpaceEventType.LEFT_UP);

  return {
    destroy: () => {
      handler.destroy();
      vertexEntities.forEach((e) => viewer.entities.remove(e));
    },
  };
};

export const makePointEditable = (viewer: Cesium.Viewer, pointEntity: Cesium.Entity, positions: Cesium.Cartesian3[], onEditCallback: () => void) => {
  pointEntity.position = new Cesium.CallbackProperty(() => positions[0], false) as unknown as Cesium.PositionProperty;

  let dragging = false;
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  const disableCamera = () => {
    const c = viewer.scene.screenSpaceCameraController;
    c.enableRotate = c.enableZoom = c.enableTranslate = false;
  };
  const enableCamera = () => {
    const c = viewer.scene.screenSpaceCameraController;
    c.enableRotate = c.enableZoom = c.enableTranslate = true;
  };

  handler.setInputAction((movement: any) => {
    const picked = viewer.scene.pick(movement.position);
    if (picked?.id !== pointEntity) return;
    dragging = true;
    disableCamera();
  }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

  handler.setInputAction((movement: any) => {
    if (!dragging) return;
    const pos = getWorldPosition(viewer, movement);
    if (!pos) return;
    positions[0] = pos;
    onEditCallback();
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(() => {
    dragging = false;
    enableCamera();
  }, Cesium.ScreenSpaceEventType.LEFT_UP);

  return {
    destroy: () => handler.destroy(),
  };
};
